/**
 * The Add slot / Fill-to-end button pair under the slot table. Fill adds
 * every slot the schedule still has room for — one per interval step from
 * the next free time through the boundary (container end time, else the
 * country window end) — and hides itself when nothing fits.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Plus } from 'lucide-react';

import { Button, HintDot } from '@/components/ui';

import { dkLabel } from '../../../../utils/clock/dk-label';
import { minutesLabel } from '../../../../utils/format';
import { useCountryClock } from '../../../shared/time/country-clock-context';

interface Props {
  fillCount: number;
  boundaryMin: number;
  intervalMin: number;
  onAdd: () => void;
  onFill: (count: number) => void;
}

export const SlotFillControls: FC<Props> = ({
  fillCount,
  boundaryMin,
  intervalMin,
  onAdd,
  onFill,
}) => {
  const { tz, runDate } = useCountryClock();
  // Danish first (15/10/2026): the boundary is the country's minute, read on the Danish clock.
  const until = dkLabel(boundaryMin, tz, runDate);
  const label = until ? `${until} 🇩🇰` : minutesLabel(boundaryMin);
  return (
  <div className="flex items-center gap-2 self-start">
    <Button variant="outline" size="sm" onClick={() => onAdd()}>
      <Plus className="mr-1 h-3.5 w-3.5" /> Add drop
    </Button>
    {fillCount > 0 ? (
      <>
        <Button variant="outline" size="sm" onClick={() => onFill(fillCount)}>
          Fill to {label} · +{fillCount}
        </Button>
        <HintDot
          label={`Adds a drop every ${intervalMin} min from the next free time through ${label} — the container's end time, or the country window when no end is set. New rows inherit the default amount; rows you've edited are untouched.`}
        />
      </>
    ) : null}
  </div>
  );
};
